import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import { Modal, validationErrMessages } from '@components/common';
import FormWithTabs from '@components/common/FormWithTabs';

import useMutateCheckEmailAsPo from '@hooks/useMutateCheckEmailAsPo';
import { useQueryGetProfileAsPo } from '@hooks/useQueryGetProfileAsPo';

import { IUserFormValues } from '@interfaces/components/form';
import { IUserEntity } from '@interfaces/user/user';
import { IOrganisationField } from './interfaces';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { EUserMultipleOrganisationsFormFields, userFormTabNames } from './constants';
import { adminRoles } from '@components/ModalComponents/UserForm/constants';

import AssignOrganisationForm from './AssignOrganisationForm';
import OrganisationAssignment from './OrganisationAssignment';
import { baseSchema } from './schema';
import { useEmailValidation } from './useEmailValidation';
import UserMultipleOrganisationsBasicInfo from './UserMultipleOrganisationsBasicInfo';

export interface IUserMultipleOrganisationsFormProps {
	editableUser?: IUserEntity;
	isEditMode: boolean;
	isViewMode: boolean;
	isLoading?: boolean;
	organisationId?: string;
	organisationName?: string;
	closeModals?: () => void;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	submitForm: (values: IUserFormValues) => void;
	toggleCloseConfirmModal?: () => void;
}

const UserMultipleOrganisationsForm = ({
	editableUser,
	isEditMode,
	isViewMode,
	isLoading,
	organisationId,
	organisationName,
	closeModals,
	setIsDirtyFormModal,
	setIsEditMode,
	submitForm,
	toggleCloseConfirmModal,
}: IUserMultipleOrganisationsFormProps) => {
	const adminRoleNames = adminRoles.map(({ role }) => role);

	const [selectedOrganisation, setSelectedOrganisation] = useState<IOrganisationField | undefined>();

	const isAdmin = editableUser?.roles?.some((item) => adminRoleNames.includes(item.name));
	const isSingleOrganisationMode = !!organisationName;

	const isLastAdmin = isSingleOrganisationMode
		? !!editableUser?.isLastAdmin
		: !!editableUser?.isLastAdminInAnyOrganization;

	const isExistingUserAdmin = editableUser ? (!isAdmin || !isLastAdmin) : true;

	const [isOrganisationAssignmentForm, setIsOrganisationAssignmentForm] = useState(false);
	const [isDirtyOrganisationAssignmentForm, setIsDirtyOrganisationAssignmentForm] = useState(false);

	const openOrganisationAssignmentForm = () => setIsOrganisationAssignmentForm(true);
	const closeOrganisationAssignmentForm = () => {
		setIsOrganisationAssignmentForm(false);
		setSelectedOrganisation(undefined);
	};

	const {
		checkEmailError,
		emailStatus,
		checkEmail,
	} = useMutateCheckEmailAsPo();

	const {
		alreadyExists = false,
		isInAnotherOrganization = false,
		isEmptyOrganizations = false,
		userId,
	} = emailStatus || {};

	const { userProfile } = useQueryGetProfileAsPo({ id: editableUser?.id || userId });

	const preparedOrganisations: IOrganisationField[] = useMemo(() => {
		return userProfile?.organizations?.map((org, index) => {
			return {
				organizationId: org.organizationId,
				isEnabled: org.isEnabled,
				jobTitle: org.jobTitle,
				isLastAdmin: org.isLastAdmin,
				isAssignedToApprovalUnits: org.isAssignedToApprovalUnits,
				avatar: org.avatar?.id,
				organisation: { value: org.organizationId, label: org.name },
				title: org.name,
				adminCount: org?.adminCount || 0,
				isActive: org.isActive || true,
				roles: org.roles?.map((role) => ({ value: role.name, label: role.title })) || [],
				index,
			};
		}) || [];
	}, [userProfile?.organizations]);

	const { validateEmail } = useEmailValidation({
		defaultValue: editableUser?.email,
		checkEmailFn: checkEmail,
		organizationId: isSingleOrganisationMode ? organisationId : undefined,
	});

	const schema = baseSchema(
		validateEmail,
		checkEmailError,
		false,
	);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		setError,
		watch,
		getValues,
		control,
	} = useForm<FieldValues>({
		mode: EValidationMode.onChange,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[EUserMultipleOrganisationsFormFields.firstName]: editableUser?.firstName || '',
			[EUserMultipleOrganisationsFormFields.lastName]: editableUser?.lastName || '',
			[EUserMultipleOrganisationsFormFields.email]: editableUser?.email || '',
			[EUserMultipleOrganisationsFormFields.phoneNumber]: editableUser?.phoneNumber || '',
			[EUserMultipleOrganisationsFormFields.is2FA]: false,
			[EUserMultipleOrganisationsFormFields.organisations]: preparedOrganisations || [],
		},
	});

	const {
		append,
		replace,
		update,
		remove,
	} = useFieldArray<FieldValues>({
		control,
		name: EUserMultipleOrganisationsFormFields.organisations,
	});

	const watchedFields: IOrganisationField[] = useWatch({
		name: EUserMultipleOrganisationsFormFields.organisations,
		control,
	});

	const isUniqueOrInAnotherOrganisation = !alreadyExists
		|| isInAnotherOrganization
		|| (!isInAnotherOrganization && isEmptyOrganizations);

	const isUniqueUserError = !isUniqueOrInAnotherOrganisation && isSingleOrganisationMode;

	const usedOrganisationIds = watchedFields.map(field => field?.organizationId);
	const singleOrganisation = editableUser?.id
		? userProfile?.organizations?.find(item => item.organizationId === organisationId)
		: undefined;

	const phoneNumber = watch(EUserMultipleOrganisationsFormFields.phoneNumber) ? `+${watch(EUserMultipleOrganisationsFormFields.phoneNumber)}` : '';

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	useEffect(() => {
		setValue(EUserMultipleOrganisationsFormFields.organisations, preparedOrganisations);
	}, [preparedOrganisations, setValue]);

	useEffect(() => {
		if (isEditMode && userProfile?.is2FA !== null) {
			setValue(EUserMultipleOrganisationsFormFields.is2FA, !!userProfile?.is2FA);
		}
	}, [userProfile?.is2FA, isEditMode, setValue]);

	useEffect(() => {
		if ((isEditMode || isViewMode) && userProfile?.phoneNumber !== null) {
			setValue(EUserMultipleOrganisationsFormFields.phoneNumber, userProfile?.phoneNumber);
		}
	}, [userProfile?.phoneNumber, isEditMode, isViewMode, setValue]);

	useEffect(() => {
		if (isUniqueUserError) {
			setError(EUserMultipleOrganisationsFormFields.email, { type: 'manual', message: validationErrMessages.emailUnique });
		}
	}, [isUniqueUserError, setError]);

	const onSubmit = useCallback(() => {
		const values = getValues();

		const data: IUserFormValues = {
			...values,
			...(userId && userProfile
				? {
					firstName: userProfile.firstName,
					lastName: userProfile.lastName,
					email: userProfile.email,
					phoneNumber: userProfile.phoneNumber,
					is2FA: userProfile.is2FA || false,
				}
				: {}
			),
		};

		const id = isEditMode ? (editableUser?.id || userProfile?.id) : userId;
		const isExistingUserContext = userId || isSingleOrganisationMode || isEditMode;

		submitForm({ ...data, id: isExistingUserContext ? id : undefined });
	}, [
		getValues,
		submitForm,
		userId,
		userProfile,
		editableUser?.id,
		isEditMode,
		isSingleOrganisationMode,
	]);

	const isEmailAlreadyExists = !!emailStatus?.alreadyExists;

	const tabs = useMemo(() => [
		{
			title: 'Basic information',
			value: userFormTabNames.basicInformation,
			component: UserMultipleOrganisationsBasicInfo,
			isTabError: !!(errors?.firstName
				|| errors?.lastName
				|| errors?.email
				|| errors?.phoneNumber
				|| errors?.is2FA
			),
			props: {
				isLoading,
				isEmailAlreadyExists,
				isSingleOrganisationMode,
				isSubmitDisabled: isUniqueUserError,
				phoneNumber,
				errors,
				isEditMode,
				isViewMode,
				register,
				onClose: toggleCloseConfirmModal,
				setError,
				setIsEditMode,
				setValue,
				onSubmit,
				handleSubmit,
				watch,
			},
		},
		{
			title: 'Organisation Assignment',
			value: userFormTabNames.organisationAssignment,
			component: OrganisationAssignment,
			isTabError: false,
			isHidden: isSingleOrganisationMode,
			props: {
				control,
				isEditMode,
				isViewMode,
				openOrganisationAssignmentForm,
				setIsEditMode,
				setSelectedOrganisation,
				remove,
				update,
				onSubmit,
			},
		},
		{
			title: 'Organisation Assignment',
			value: userFormTabNames.assignForm,
			component: AssignOrganisationForm,
			isTabError: false,
			isHidden: !isSingleOrganisationMode,
			props: {
				editableUser,
				assignedOrganisation: {
					organizationId: organisationId,
					title: organisationName,
					roles: singleOrganisation?.roles?.map((role) => ({ value: role.name, label: role.title })) || [],
					jobTitle: singleOrganisation?.jobTitle || '',
					organisation: { value: organisationId, label: organisationName },
					isEnabled: !!singleOrganisation?.isEnabled,
				},
				isExistUserAdmin: isExistingUserAdmin,
				usedOrganisationIds,
				isSingleOrganisationMode,
				isEditMode,
				isViewMode,
				setIsEditMode,
				setIsDirtyFormModal,
				closeModals,
				append,
				replace,
				onSubmit,
			},
		},
	], [
		errors,
		isLoading,
		isEmailAlreadyExists,
		isSingleOrganisationMode,
		isUniqueUserError,
		phoneNumber,
		isEditMode,
		isViewMode,
		register,
		toggleCloseConfirmModal,
		setError,
		setIsEditMode,
		setValue,
		onSubmit,
		handleSubmit,
		watch,
		control,
		remove,
		update,
		editableUser,
		organisationId,
		organisationName,
		singleOrganisation?.roles,
		singleOrganisation?.jobTitle,
		singleOrganisation?.isEnabled,
		isExistingUserAdmin,
		usedOrganisationIds,
		setIsDirtyFormModal,
		closeModals,
		append,
		replace,
	]);

	return (
		<>
			<FormWithTabs
				isEditMode={isEditMode || isViewMode}
				stickyTabs
				tabs={tabs}
			/>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyOrganisationAssignmentForm}
				isOpen={isOrganisationAssignmentForm}
				maxWidth={MODAL_WIDTH_480}
				onClose={closeOrganisationAssignmentForm}
				title='Assign organisation'
				width='auto'
			>
				<AssignOrganisationForm
					append={append}
					assignedOrganisation={selectedOrganisation}
					closeModals={closeOrganisationAssignmentForm}
					editableUser={editableUser}
					isEditMode={isEditMode || !!selectedOrganisation}
					isExistUserAdmin={isExistingUserAdmin}
					isSingleOrganisationMode={isSingleOrganisationMode}
					onSubmit={onSubmit}
					replace={replace}
					setIsDirtyFormModal={setIsDirtyOrganisationAssignmentForm}
					setIsEditMode={setIsEditMode}
					update={update}
					usedOrganisationIds={usedOrganisationIds}
				/>
			</Modal>
		</>
	);
};

export default UserMultipleOrganisationsForm;
